/**
 * Turn a video page link into the URL an <iframe> can load.
 *
 * Guide videos are configured as plain share links in `guideVideos.ts` (the
 * link you copy from the browser bar), so nobody has to know a provider's
 * embed scheme to publish one. Returns null when the host is not one we embed
 * (or the input is not a URL at all); the card then falls back to opening the
 * link in a new tab instead of showing a broken frame.
 */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,}$/

function youTubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, '')
  if (host === 'youtu.be') {
    return url.pathname.slice(1).split('/')[0] || null
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const fromQuery = url.searchParams.get('v')
    if (fromQuery) return fromQuery
    const m = url.pathname.match(/^\/(?:embed|shorts|live)\/([^/?]+)/)
    return m ? m[1] : null
  }
  return null
}

/**
 * The video's own poster frame for the card, or null when the host has no
 * public thumbnail URL we can build without an API call. YouTube serves
 * `hqdefault.jpg` (480x360) for every public video; `maxresdefault.jpg`
 * exists only for uploads that have an HD frame, so the card uses the one
 * that is always there. Vimeo and Loom need an API lookup, so their cards
 * keep the plain play button.
 */
export function toThumbnailUrl(raw: string): string | null {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return null
  }
  const yt = youTubeId(url)
  if (yt && YOUTUBE_ID.test(yt)) return `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`
  return null
}

export function toEmbedUrl(raw: string): string | null {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

  const yt = youTubeId(url)
  // Served from www.youtube.com, not the privacy-enhanced youtube-nocookie.com
  // host it used until 2026-10-05: corporate web filters commonly allow
  // youtube.com but intercept youtube-nocookie.com (seen on the office
  // FortiGate, which answered the embed with its own certificate), and a
  // frame the browser cannot trust shows only "this page might be down".
  // `rel=0` keeps the end-of-video suggestions to the same channel.
  if (yt) return YOUTUBE_ID.test(yt) ? `https://www.youtube.com/embed/${yt}?rel=0` : null

  const host = url.hostname.replace(/^www\./, '')
  if (host === 'vimeo.com') {
    const m = url.pathname.match(/^\/(\d+)/)
    return m ? `https://player.vimeo.com/video/${m[1]}` : null
  }
  if (host === 'player.vimeo.com') {
    const m = url.pathname.match(/^\/video\/(\d+)/)
    return m ? `https://player.vimeo.com/video/${m[1]}` : null
  }
  if (host === 'loom.com') {
    const m = url.pathname.match(/^\/(?:share|embed)\/([A-Za-z0-9]+)/)
    return m ? `https://www.loom.com/embed/${m[1]}` : null
  }
  return null
}
